import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwp211Api, FP120, FP121, FP122, FP123, ValidationError, CreateRequest } from '../../../services/pwp211Api';

const emptyMaster = (): FP120 => ({
  fp120_01: '', fp120_10: '', fp120_11: null, fp120_12: '', fp120_131: '', fp120_132: '',
  fp120_14: '', fp120_15: 0, fp120_151: 0, fp120_16: 0, fp120_161: 0, fp120_17: 0, fp120_18: 0,
  fp120_19: '', fp120_20: false, fp120_21: '', fp120_22: 0, fp120_23: 0, fp120_24: '',
  fp120_25: '', fp120_26: null, fp120_27: null, fp120_28: 0, fp120_29: null, fp120_30: false,
  fp120_31: 0, fp120_80: '', fp120_91: '', fp120_92: null, fp120_93: '',
});

export function usePWP211Record() {
  const [record, setRecord] = useState<FP120 | null>(null);
  const [formData, setFormData] = useState<FP120>(emptyMaster());
  const [judgments, setJudgments] = useState<FP121[]>([]);
  const [defects, setDefects] = useState<FP122[]>([]);
  const [basicInfos, setBasicInfos] = useState<FP123[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwp211Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setJudgments(data.judgments || []);
      setDefects(data.defects || []);
      setBasicInfos(data.basic_infos || []);
    } catch (err) {
      message.error('載入記錄失敗');
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData(emptyMaster());
    setJudgments([]);
    setDefects([]);
    setBasicInfos([]);
    setValidationErrors([]);
  }, []);

  const loadSourceDraft = useCallback(async (sourceNo: string): Promise<boolean> => {
    const normalized = sourceNo.trim();
    if (!normalized) {
      message.warning('請先輸入繳庫單或託工入庫單號');
      return false;
    }
    setLoading(true);
    setValidationErrors([]);
    try {
      const draft = await pwp211Api.previewFromSource(normalized);
      setRecord(null);
      setFormData({ ...emptyMaster(), ...draft, fp120_24: normalized });
      setJudgments(draft.judgments || []);
      setDefects(draft.defects || []);
      setBasicInfos(draft.basic_infos || []);
      message.success(`已從來源單 ${normalized} 帶入 ${draft.basic_infos?.length || 0} 筆明細`);
      return true;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        message.error(axiosErr.response?.data?.error || '來源單帶入失敗');
      } else {
        message.error('來源單帶入失敗');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setJudgments(record.judgments || []);
      setDefects(record.defects || []);
      setBasicInfos(record.basic_infos || []);
    }
    setValidationErrors([]);
  }, [record]);

  const handleSave = useCallback(async (mode: 'add' | 'edit'): Promise<FP120 | null> => {
    setValidationErrors([]);
    const req: CreateRequest = {
      master: formData,
      judgments,
      defects,
      basic_infos: basicInfos,
    };
    try {
      let saved: FP120;
      if (mode === 'add') {
        saved = await pwp211Api.create(req);
      } else {
        saved = await pwp211Api.update(formData.fp120_01, req);
      }
      setRecord(saved);
      setFormData({ ...saved });
      setJudgments(saved.judgments || []);
      setDefects(saved.defects || []);
      setBasicInfos(saved.basic_infos || []);
      message.success('存檔成功');
      return saved;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { errors?: ValidationError[]; error?: string } } };
        if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
          setValidationErrors(axiosErr.response.data.errors);
          const msgs = axiosErr.response.data.errors.map((e: ValidationError) => e.message).join('\n');
          message.error(msgs);
        } else {
          message.error(axiosErr.response?.data?.error || '存檔失敗');
        }
      } else {
        message.error('存檔失敗');
      }
      return null;
    }
  }, [formData, judgments, defects, basicInfos]);

  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pwp211Api.delete(record.fp120_01);
      message.success('刪除成功');
      setRecord(null);
      setFormData(emptyMaster());
      setJudgments([]);
      setDefects([]);
      setBasicInfos([]);
      return true;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        message.error(axiosErr.response?.data?.error || '刪除失敗');
      } else {
        message.error('刪除失敗');
      }
      return false;
    }
  }, [record]);

  const getField = useCallback((field: keyof FP120) => formData[field], [formData]);

  const setField = useCallback((field: keyof FP120, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Judgment row management
  const addJudgmentRow = useCallback(() => {
    const newRow: FP121 = {
      fp121_01: formData.fp120_01, fp121_02: judgments.length + 1,
      fp121_111: 0, fp121_112: 0, fp121_113: 0, fp121_12: 0,
      fp121_131: 0, fp121_132: 0, fp121_133: 0, fp121_134: 0,
      fp121_14: 0, fp121_15: 0, fp121_16: 0, fp121_17: 0, fp121_18: 0,
      fp121_19: '', fp121_20: 0, fp121_21: '', fp121_22: '', fp121_23: '',
      fp121_24: '', fp121_25: 0, fp121_26: false, fp121_27: 0,
      fp121_28: '', fp121_29: 0, fp121_30: '', fp121_241: '',
      fp121_141: 0, fp121_151: 0, fp121_142: 0, fp121_152: 0,
      fp121_31: '', fp121_32: '', fp121_33: '', fp121_34: '',
      fp121_91: '', fp121_92: null, fp121_93: '',
    };
    setJudgments(prev => [...prev, newRow]);
  }, [formData.fp120_01, judgments.length]);

  const deleteJudgmentRow = useCallback((seq: number) => {
    setJudgments(prev => prev.filter(j => j.fp121_02 !== seq).map((j, i) => ({ ...j, fp121_02: i + 1 })));
    setDefects(prev => prev.filter(d => d.fp122_02 !== seq));
  }, []);

  const updateJudgmentField = useCallback((seq: number, field: keyof FP121, value: unknown) => {
    setJudgments(prev => prev.map(j => j.fp121_02 === seq ? { ...j, [field]: value } : j));
  }, []);

  return {
    record, formData, judgments, defects, basicInfos, loading, validationErrors,
    loadRecord, resetForAdd, loadSourceDraft, revertToRecord, handleSave, handleDelete,
    getField, setField, setJudgments, setDefects, setBasicInfos,
    addJudgmentRow, deleteJudgmentRow, updateJudgmentField,
  };
}
